import { useCallback, useMemo } from 'react';

/**
 * Tab state as a query param, router-agnostic by construction (the caller
 * passes `useSearchParams()`'s values in — see `useUrlTableState`'s header
 * comment; packages/ui must not import react-router, see
 * docs/feature-authoring.md).
 *
 * Deliberately narrower than `useUrlTableState`'s `setSearchParams`: this
 * hook only ever writes via an *updater function* (never a plain
 * `URLSearchParams` value), so the injected setter's type reflects that —
 * see the updater-vs-snapshot rationale on `useUrlTableState`, which applies
 * identically here: a tab body can itself hold a `DataTable` writing
 * `sort`/`f.*`/`q`/`page` to the same URL in the same tick, and basing the
 * write on the router's own `prev` (rather than a `searchParams` closed over
 * at hook-render time) is what makes both writers' changes merge instead of
 * one clobbering the other.
 */
export interface UseUrlTabStateOptions {
  /** Query param name. Defaults to `tab`. */
  param?: string;
  /** The set of valid tab keys. A URL value outside this set is treated as absent. */
  keys: string[];
  /** Used when the URL carries no recognized tab. */
  defaultKey: string;
}

/**
 * Reads the active tab out of `param` (validated against `keys`, else
 * `defaultKey`), and returns a setter that writes it back with
 * `replace: true` — a tab switch does not deserve its own back-button stop,
 * matching `useUrlTableState`'s treatment of a debounced search-only change.
 *
 * The setter clears/overwrites ONLY `param`; every other param on the URL
 * (including one a tab body's own `DataTable` is driving on this same
 * route) passes through untouched.
 */
export function useUrlTabState(
  searchParams: URLSearchParams,
  setSearchParams: (
    updater: (prev: URLSearchParams) => URLSearchParams,
    opts?: { replace?: boolean },
  ) => void,
  opts: UseUrlTabStateOptions,
): [string, (key: string) => void] {
  const { param = 'tab', keys, defaultKey } = opts;
  const keysJoined = keys.join(',');

  const activeTab = useMemo(() => {
    const raw = searchParams.get(param);
    return raw !== null && keys.includes(raw) ? raw : defaultKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString(), param, keysJoined, defaultKey]);

  const setActiveTab = useCallback(
    (key: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(param, key);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams, param],
  );

  return [activeTab, setActiveTab];
}
